import { Component, TemplateRef, ViewChild, OnInit, AfterViewInit, AfterViewChecked, inject, ElementRef, ChangeDetectorRef, Input, Output, EventEmitter, DestroyRef, effect, computed, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { AppInput } from '../../../shared/app-input/app-input';
import { AppTooltipComponent } from '../../../shared/app-tooltip/app-tooltip.component';
import { QUOTE_FLOW, quoteAccordion, QuoteFlowEnum, QuoteSource } from '../quote-builder.constant';
import { Images } from '../../../core/constants/image.constants';
import { ChatMessage, QuoteData, QuoteDrawerService } from '../services/quote-drawer.service';
import { ApiService } from '../../../core/services/api.service';
import { AiChatHistoryItem, AiChatRequest, AiChatRequestUpdated, AiChatResponse } from '../../models/ai-chat-request-model';
import { Router } from '@angular/router';
import { PreferenceForm } from '../../../core/services/preference-form';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationReviewService } from '../../services/application-review.service';
import { CrmApiService } from '../../../core/services/shared-services/crm-service/crm-api.service';
import { SharedMasterDataApiService } from '../../../core/services/shared-services/shared-master-service/shared-master-data-api.service';
import { leadId, leadInfoId } from '../../../core/constants/auth-state';
import { DateTimeConversionHelper } from '../../../shared/helper/date-time-conversion.helper';

@Component({
  selector: 'app-quote-drawer',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    AppInput,
    AppTooltipComponent
  ],
  templateUrl: './quote-drawer.component.html',
  styleUrl: './quote-drawer.component.scss'
})
export class QuoteDrawerComponent implements OnInit, AfterViewInit, AfterViewChecked {
  @ViewChild('leadSummaryContent', { static: false })
  leadSummaryContentTpl!: TemplateRef<any>;
  @ViewChild('feedbackContent', { static: false })
  feedbackContentTpl!: TemplateRef<any>;
  @ViewChild('productLibraryContent', { static: false })
  productLibraryContentTpl!: TemplateRef<any>;
  @ViewChild('aiAssistantContent', { static: false })
  aiAssistantContentTpl!: TemplateRef<any>;
  @ViewChild('chatScroll') chatScroll!: ElementRef<HTMLDivElement>;

  private drawerService = inject(QuoteDrawerService);
  private fb = inject(FormBuilder);
  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private readonly crmApiService = inject(CrmApiService);
  private readonly sharedMasterService = inject(SharedMasterDataApiService);

  @Output() planAdded = new EventEmitter<{ plan: any; topThreePlans: any[] }>();
  @Input() isComparison: boolean = false;

  accordionOpen = [true, false, false, false];
  accordionSections = [...quoteAccordion];
  images = Images;

  // Use service signals
  quoteData = this.drawerService.quoteData;
  feedbackData = this.drawerService.feedbackData;
  productCards = this.drawerService.productCards;
  versionHistory = this.drawerService.versionHistory;
  hasOnlyDraftVersion = computed(() => {
    const versions = this.versionHistory();
    return versions.some((item) => item.isDraft === true)
  });

  searchForm!: FormGroup;
  feedbackUserName = 'User Name';
  private shouldScrollAI = false;
  searchQuery: string = '';
  searchValidationMessage: string | null = null;
  feedbackIndex = 1;
  requote = true;
  readonly MINIMUM_SEARCH_CHARACTERS = 3;

  private aiConversationLoaded = false;
  isVersionHistoryMode = false;
  selectedTimelineVersionKey: string | null = null;
  hasChatStarted = false;
  chatInputText: string = '';
  userInitials: string = '';
  chatMessages: ChatMessage[] = [];
  topThreePlans: any[] = [];
  leadData: any = null;
  prefFormData: any = null;
  productLibraryData: any = null;
  versionQuotesResponse: any = null;
  private latestProductLibraryRequestId = 0;
  isProductLibraryLoading = false;



  hasUnsavedChanges = this.drawerService.hasUnsavedChanges;

  constructor(
    private preferenceService: PreferenceForm,
  ) {
    effect(() => {
      const action = this.drawerService.drawerAction();
      if (!action) return;

      // untracked: prevents signals read/written while handling the action (e.g. currentVersion)
      // from becoming dependencies of this effect, which would otherwise re-trigger it endlessly
      untracked(() => {
        if (action.type === 'open-version-history') {
          this.isVersionHistoryMode = true;
          this.drawerService.setVersionHistoryMode(true);
          this.setDefaultVersionSelection();
          const selectedVersion = this.drawerService.currentVersion()?.version;
          const storedQuotes = this.drawerService.quotesStoredForLead();
          if (selectedVersion && storedQuotes.length) {
            this.fetchQuotesForVersion(selectedVersion);
          }
        } else if (action.type === 'close-version-history') {
          this.isVersionHistoryMode = false;
          this.selectedTimelineVersionKey = null;
          this.drawerService.setVersionHistoryMode(false);
        } else if (action.type === 'open-feedback') {
          this.isVersionHistoryMode = false;
          this.drawerService.setVersionHistoryMode(false);
          this.accordionOpen = [false, true, false, false];
        }

        this.drawerService.clearDrawerAction();
      });
    });

    effect(() => {
      const quotesStoredForLead = this.drawerService.quotesStoredForLead();

      if (!quotesStoredForLead.length) {
        return;
      }

      // untracked: getVersionHistory reads/writes versionHistory & currentVersion signals;
      // without untracked, those become effect dependencies and the effect re-fires in a loop
      untracked(() => {
        if (this.drawerService.hasUnsavedChanges()) {
          return;
        }

        if (this.isVersionHistoryMode) {
          const selectedVersion = this.drawerService.currentVersion()?.version;
          if (selectedVersion) {
            this.fetchQuotesForVersion(selectedVersion);
          }
          return;
        }

        this.getVersionHistory();
      });
    });
  }



  ngOnInit(): void {
    // For comparison mode, only show lead summary
    if (this.isComparison) {
      this.accordionOpen = [true];
      this.accordionSections = [quoteAccordion[0]];
    }

    this.initializeLeadData();
    this.getPreferenceFormDetails();

    if (!this.isComparison) {
      this.getFeedbackSection();
    }
    this.searchForm = this.fb.group({
      search: ['']
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  getPreferenceFormDetails(): void {
    const currentleadInfoId: any = this.isComparison ? leadInfoId() : this.leadData?.id || '';
    if (!currentleadInfoId) return;

    forkJoin({
      applicants: this.crmApiService.getApplicantInfoByLeadInfo(currentleadInfoId),
      emirates: this.sharedMasterService.getVisaMaster(),
      insurers: this.sharedMasterService.getInsurerMaster(),
      preference: this.crmApiService.getPreferenceByLeadInfoId(currentleadInfoId),
    }).pipe(
      switchMap((firstPass) => {
        const rawPref = firstPass.preference ?? {};
        const preferenceData = (rawPref as any)?.body ?? rawPref;
        const preferenceId = String(preferenceData?.id ?? preferenceData?.preferenceId ?? (rawPref as any)?.id ?? '').trim();

        const providerMap$ = preferenceId
          ? this.crmApiService.getPreferenceProviderMap(preferenceId)
          : of([]);

        return providerMap$.pipe(
          map((providerMaps) => {
            const applicants = Array.isArray(firstPass.applicants)
              ? firstPass.applicants
              : ((firstPass.applicants as any)?.body ?? []);
            const emirates = Array.isArray(firstPass.emirates)
              ? firstPass.emirates
              : ((firstPass.emirates as any)?.body ?? []);
            const insurers = Array.isArray(firstPass.insurers)
              ? firstPass.insurers
              : ((firstPass.insurers as any)?.body ?? []);
            const providerMapsList = Array.isArray(providerMaps)
              ? providerMaps
              : ((providerMaps as any)?.body ?? []);

            const stitchedPayload = {
              leadId: this.isComparison ? leadId() : this.leadData?.leadId,
              leadInfoId: currentleadInfoId,
              applicants,
              emirates,
              insurers,
              ...(preferenceData || {}),
              preferenceProviderMaps: providerMapsList,
            };

            return stitchedPayload;
          })
        );
      }),
      switchMap((stitchedPayload) => this.preferenceService.getClientProfilePreferenceForm(stitchedPayload).pipe(
        map((res) => res?.body ?? res ?? stitchedPayload)
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (response: any) => {
        if (!response) return;
        this.prefFormData = response;
        this.drawerService.setClientProfileResponse(response);
        this.drawerService.clientProfileData.set(response);
        this.mapPrefToLeadSummary();
      },
      error: (err) => {
        console.error('Error fetching client profile preference form details:', err);
      }
    });
  }

  getProductLibrary(searchQuery: string = ''): void {
    const payload = { SearchQuery: searchQuery };
    this.latestProductLibraryRequestId += 1;
    const requestId = this.latestProductLibraryRequestId;
    this.isProductLibraryLoading = true;

    this.drawerService.subscribeWithCleanup(
      this.drawerService.getProductLibrary(payload).pipe(
        catchError(() => of(null)),
      ),
      (response: any) => {
        if (requestId !== this.latestProductLibraryRequestId) return;
        this.isProductLibraryLoading = false;
        if (!response?.body) return;
        this.productLibraryData = response.body;
      },
      this.destroyRef
    );
  }

  getFeedbackSection(): void {
    const leadId = this.leadData?.id;
    this.drawerService.subscribeWithCleanup(
      this.drawerService.getFeedback(leadId),
      (response: any) => {
        if (!response?.body) return;
        const feedbacks = response.body.feedbacks || [];
        const mappedFeedbacks = feedbacks.map((item: any, index: number) => ({
          id: `feedback-${index}`,
          date: item.date,
          message: item.notes,
          timestamp: new Date(item.date)
        }));
        this.drawerService.setFeedbackData(mappedFeedbacks);
      },
      this.destroyRef
    );
  }


  private initializeLeadData(): void {
    const navigationState = this.router.currentNavigation()?.extras?.state;
    const state = navigationState ?? history.state;
    this.leadData = state?.leadData ?? null;
    const clientName = this.leadData?.clientName;
    this.userInitials = clientName ? this.getInitials(clientName) : '';
  }

  ngAfterViewInit(): void {
    // Only set templates for sections that exist
    if (this.accordionSections.length > 0) {
      this.accordionSections[0].contentTpl = this.leadSummaryContentTpl;
    }
    if (this.accordionSections.length > 1) {
      this.accordionSections[1].contentTpl = this.feedbackContentTpl;
    }
    if (this.accordionSections.length > 2) {
      this.accordionSections[2].contentTpl = this.productLibraryContentTpl;
    }
    if (this.accordionSections.length > 3) {
      this.accordionSections[3].contentTpl = this.aiAssistantContentTpl;
    }
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollAI && this.chatScroll) {
      this.scrollToBottom();
      this.shouldScrollAI = false;
    }
  }

  closeVersionHistory(): void {
    this.isVersionHistoryMode = false;
    this.selectedTimelineVersionKey = null;
    this.drawerService.setVersionHistoryMode(false);

    const hasDraftVersion = this.versionHistory().some((item) => item.isDraft === true);
    if (hasDraftVersion) {
      // For draft, call without version parameter
      this.getQuotesInfoOnlyByVersion({ leadInfoId: this.leadData?.id, quoteversion: "draft" });
      this.setDefaultVersionSelection();
    } else {
      // No draft version - clear product cards
      this.drawerService.setProductCards([]);
    }
  }

  private getTimelineItemKey(item: { version: string; createdOnDate: string }): string {
    return `${item.version}-${item.createdOnDate}`;
  }

  private setDefaultVersionSelection(): void {
    const versions = this.versionHistory();
    if (!versions.length) {
      this.selectedTimelineVersionKey = null;
      return;
    }

    const draftVersion = versions.find((v: any) => v.isDraft === true);
    const currentVersion = this.drawerService.currentVersion();
    const matchedCurrent = currentVersion
      ? versions.find((v: any) => this.getTimelineItemKey(v) === this.getTimelineItemKey(currentVersion))
      : null;

    const selected = draftVersion || matchedCurrent || versions[0];
    this.selectedTimelineVersionKey = this.getTimelineItemKey(selected);

    this.drawerService.setCurrentVersion({
      version: selected.version,
      createdOnDate: selected.createdOnDate,
      modifiedOnDate: selected.modifiedOnDate ?? selected.createdOnDate,
      quotes: selected.quotes !== undefined ? String(selected.quotes) : '',
      isDraft: selected.isDraft ?? false
    });
  }

  selectTimelineItem(item: { version: string; createdOnDate: string; modifiedOnDate?: string; quotes?: string | number; isDraft?: boolean }): void {
    this.selectedTimelineVersionKey = this.getTimelineItemKey(item);
    // Update current version in service so other components can access it
    this.drawerService.setCurrentVersion({
      version: item.version,
      createdOnDate: item.createdOnDate,
      modifiedOnDate: item.modifiedOnDate ?? item.createdOnDate,
      quotes: item.quotes !== undefined ? String(item.quotes) : '',
      isDraft: item.isDraft ?? false
    });

    // Fetch quotes for the selected version
    this.getQuotesInfoOnlyByVersion({ leadInfoId: this.leadData?.id, quoteversion: item.version });
  }

  fetchQuotesForVersion(version: string): void {
    const leadId = this.leadData?.id;
    if (!leadId) return;
    const lead = {
      id: this.leadData.id,
      leadId: this.leadData.leadId,
      leadStatus: this.leadData.leadStatus
    };

    this.drawerService.subscribeWithCleanup(
      this.drawerService.getQuotesByVersion(lead, version, undefined, false, this.drawerService.quotesStoredForLead()),
      (response: any) => {
        if (!response?.body) return;

        // Store the complete response for use in AI chat
        this.versionQuotesResponse = response;

        const quotes = response.body.versions[0]?.quotes || [];
        const newCards = quotes.map((item: any) => {
          const quote = item.quote || {};
          const product = item.product || {};
          const productHealth = item.productHealth || {};
           const applicantRates = this.drawerService.mapApplicantRates(
              quote.applicantRates ?? productHealth.applicantRates
            );
          return {
            product: {
              id: product.id,
              productCode: product.productCode,
              productName: product.productName,
              insurer: product.insurer,
              currency: product.currency || 'AED',
              colorCode: product.colorCode
            },
            productHealthDetails: {
              ...productHealth,
              tobFilePath: product.tobFilePath || productHealth.tobFilePath || '',
              totalPremiumWithTaxes: quote.totalPremiumWithTaxes || productHealth.totalPremiumWithTaxes || '0',
              totalPremium: quote.totalPremium || productHealth.totalPremium || '0',
              basePremium: quote.basePremium || productHealth.basePremium || '0',
              discountAmount: quote.discountAmount || productHealth.discountAmount || '0',
              basmah: quote.basmah || productHealth.basmah || '0',
              tax: quote.tax || productHealth.tax || '0',
              applicantRates,
              rateVersion: quote.rateVersion || productHealth.rateVersion || '-',
            },
            plan: {
              productCode: product.productCode,
              rank: quote.rank || 0,
              confidenceScore: parseFloat(quote.confidenceScore) || 0,
              commission: parseFloat(quote.commission) || 0,
              reason: quote.reason || [],
              quoteSource: quote.quoteSource === 'AI' ? QuoteSource.AI : QuoteSource.Manual
            },
            colorClass: this.drawerService.decideColorClass(product.colorCode),
            logo: this.drawerService.decideLogo(product.insurer),
            favorited: quote.isFavourite || false,
            hideConfidenceScore: quote.confidenceScore != 0 && (quote.quoteSource === 'AI' || quote.quoteSource !== 1) ? false : true
          };
        });
        // Update product cards with the version's quotes
        this.drawerService.setProductCards(newCards);

      },
      this.destroyRef
    );
  }

  isTimelineItemSelected(item: { version: string; createdOnDate: string }): boolean {
    return this.selectedTimelineVersionKey === this.getTimelineItemKey(item);
  }

  // currentVersion() can still be null when a plan is added before the version-history
  // API (triggered on the parent's init) resolves, so fall back to versionHistory() directly.
  private resolveVersionForPlan(plan?: any): string {
    const currentVersion = this.drawerService.currentVersion();
    if (currentVersion?.version) {
      return currentVersion.version;
    }

    const versions = this.versionHistory();
    const fallbackVersion = versions.find((v: any) => v.isDraft === true) ?? versions[0];

    return fallbackVersion?.version || plan?.versionNumber || '';
  }

  getLogo(insurer: string) {
    return this.drawerService.decideLogo(insurer);
  }

  openAccordion(idx: number): void {
    if (idx === this.feedbackIndex && !this.requote) {
      return;
    }

    const wasOpen = this.accordionOpen[idx];
    this.accordionOpen = this.accordionOpen.map((open, i) => i === idx ? !open : false);

    // AI Assistant accordion
    if (idx === 3 && !wasOpen && !this.aiConversationLoaded) {
      this.aiConversationLoaded = true;
      this.shouldScrollAI = true;
      this.loadAiConversation();
    }
  }

  getAccordionTooltip(section: any): string {
    switch (section.title) {
      case 'Product Library':
        return 'Refresh Data';

      case 'AI Assistant':
        return 'Clear Chat';

      default:
        return '';
    }
  }

  onAccordionIconsClick(event: Event, section: any): void {
    event.stopPropagation();

    switch (section.title) {
      case 'Product Library':
        this.refreshProductLibrary();
        break;

      case 'AI Assistant':
        this.clearAiAssistant();
        break;

      default:
        break;
    }
  }

  refreshProductLibrary(): void {
    console.log('Refreshing Product Library...');
  }

  refreshAiAssistant(): void {
    console.log('Refreshing AI Assistant...');
    this.hasChatStarted = false;
    this.chatMessages = [];
  }

  private clearAiAssistant(): void {
    const leadId = this.leadData?.leadId;

    if (!leadId) {
      console.error('Lead ID is missing. Unable to clear AI chat history.');
      return;
    }

    this.apiService.deleteAiChatHistory(leadId).subscribe({
      next: () => {
        this.hasChatStarted = false;
        this.chatMessages = [];
        this.aiConversationLoaded = false;
      },
      error: (err: any) => {
        console.error('Failed to clear AI chat history', err);
      }
    });
  }

  getInitials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  isTextOverflowing(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }
    return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
  }

  getDateGroup(date: string): string {
    const today = new Date();
    const messageDate = new Date(date);
    const diffTime = Math.abs(today.getTime() - messageDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return messageDate.toLocaleDateString();
  }

  onAddPlan(plan?: any, planId?: string): void {
    // Check if plan already exists
    if (this.isPlanAdded(plan?.planId) || (planId && this.isPlanAdded(planId))) {
      return;
    }
    const productId = plan?.productId || planId;
    const selectedPlan = this.topThreePlans.find(p => p.productCode === plan?.planId);
    const leadId = this.leadData?.id || this.leadData?.leadId;
    // const basePremium = selectedPlan?.productHealthDetails[0]?.basePremium || null;
    const basePremium = parseFloat(String(selectedPlan?.productHealthDetails[0]?.basePremium ?? '0').replace(/,/g, ''));
    const applicantRates = selectedPlan?.productHealthDetails[0]?.applicantRates || null;
    const rateVersion = selectedPlan?.productHealthDetails[0]?.rateVersion || null;
    const commission = parseFloat(String(selectedPlan?.commission ?? '0').replace(/,/g, ''));
    const source = planId ? 'Manual' : (selectedPlan?.quoteSource || 'Manual');
    const applicants = this.drawerService.applicantInfo() || null;
    const quotes = this.drawerService.getStoreQuotesResponse() || [];
    const emirates = this.drawerService.emirate() || [];
    if (productId && leadId) {
      const apiCall = planId
        ? this.apiService.getSelectedQuoteInfoforProductLibrary({ productId: planId, leadInfoId: leadId, source, applicants, emirates, quotes, rateVersion, applicantRates })
        : this.apiService.getSelectedQuoteInfoforProductLibrary({ productId, leadInfoId: leadId, source, basePremium, commission, applicants, emirates, quotes, applicantRates, rateVersion });

      apiCall.subscribe({
        next: (response: any) => {
          if (response?.body?.versions?.[0]?.quotes?.[0]) {
            const quoteData = response.body.versions[0].quotes[0];
            const product = quoteData.product || {};
            
            const productHealth = quoteData.productHealth || {};
            const quote = quoteData.quote || {};
            const applicantRates = this.drawerService.mapApplicantRates(
              quote.applicantRates ?? productHealth.applicantRates
            );
            const mappedCard = {
              product: {
                id: product.id,
                productCode: product.productCode,
                productName: product.productName,
                insurer: product.insurer,
                currency: product.currency || 'AED',
                colorCode: product.colorCode
              },
              productHealthDetails: {
                ...productHealth,
                tobFilePath: product.tobFilePath || productHealth.tobFilePath || '',
                totalPremiumWithTaxes: quote.totalPremiumWithTaxes || productHealth.totalPremiumWithTaxes || '0',
                totalPremium: quote.totalPremium || productHealth.totalPremium || '0',
                basePremium: quote.basePremium || productHealth.basePremium || '0',
                discountAmount: quote.discountAmount || productHealth.discountAmount || '0',
                basmah: quote.basmah || productHealth.basmah || '0',
                tax: quote.tax || productHealth.tax || '0',
                applicantRates,
                rateVersion: quote.rateVersion || '',
              },
              plan: {
                productCode: product.productCode,
                rank: quote.rank || 0,
                confidenceScore: parseFloat(quote.confidenceScore) || 0,
                commission: parseFloat(quote.commission) || 0,
                reason: quote.reason || [],
                quoteSource: this.resolveQuoteSource(quote.quoteSource)
              },
              colorClass: this.drawerService.decideColorClass(product.colorCode),
              logo: this.drawerService.decideLogo(product.insurer),
              favorited: quote.isFavourite || false,
              hideConfidenceScore: (parseFloat(quote.confidenceScore) || 0) !== 0 ? false : true
            };

            this.drawerService.updateProductCards(cards => [...cards, mappedCard]);
            this.drawerService.setStoreQuotesResponse([mappedCard]);
            this.drawerService.markAsChanged();
          } else {
            console.error('Failed to parse quote info');
          }
        },
        error: (err: any) => console.error('Failed to get selected quote info', err)
      });
    } else {
      console.error('Could not find productId for the plan or leadId is missing.');
    }
  }

  onSearchChange(value: string): void {
    if (this.isProductLibraryLoading) {
      return;
    }

    const trimmedValue = value.trim();
    if (trimmedValue.length > 0 && trimmedValue.length < this.MINIMUM_SEARCH_CHARACTERS) {
      this.searchValidationMessage = `Minimum ${this.MINIMUM_SEARCH_CHARACTERS} characters required.`;
      return;
    }

    this.searchValidationMessage = null;
  }

  onSearchClick(): void {
    if (this.isProductLibraryLoading) {
      return;
    }

    const searchValue = this.getSearchInputValue();
    if (searchValue.length < this.MINIMUM_SEARCH_CHARACTERS) {
      this.searchValidationMessage = `Minimum ${this.MINIMUM_SEARCH_CHARACTERS} characters required.`;
      return;
    }

    this.searchValidationMessage = null;
    this.searchQuery = searchValue;
    this.getProductLibrary(searchValue);
  }

  onClearSearch(): void {
    if (this.isProductLibraryLoading) {
      return;
    }

    this.searchQuery = '';
    this.searchValidationMessage = null;
    this.searchForm.get('search')?.setValue('');
    this.productLibraryData = null;
  }

  isSearchActionDisabled(): boolean {
    return this.isProductLibraryLoading || this.getSearchInputValue().length < this.MINIMUM_SEARCH_CHARACTERS;
  }

  getSearchInputValue(): string {
    const searchControlValue = this.searchForm?.get('search')?.value;
    return typeof searchControlValue === 'string' ? searchControlValue.trim() : '';
  }

  isPlanAdded(planId: string): boolean {
    return this.productCards().some((card: any) =>
      card.plan?.productCode === planId ||
      card.product?.id === planId ||
      card.product?.productCode === planId
    );
  }

  onAddProductLibraryPlan(product: any): void {
    const productId = product.product?.id;
    const productCode = product.product?.productCode;

    if (this.isPlanAdded(productCode) || this.isPlanAdded(productId)) {
      return;
    }
    this.onAddPlan(undefined, productId);


    // const mappedCard = {
    //   product: {
    //     id: product.product?.id,
    //     productCode: productCode,
    //     productName: product.product?.productName,
    //     insurer: product.product?.insurer,
    //     currency: product.product?.currency,
    //     colorCode: product.product?.colorCode
    //   },

    //   productHealthDetails: {
    //     id: product.id,
    //     productId: product.product?.id,
    //     productCode: product.product?.productCode,
    //     tpa: product.tpa,
    //     tpaId: product.tpaId,
    //     plan: product.plan,
    //     network: product.network,
    //     networkFilePath: product.networkFilePath,
    //     coverageArea: product.coverageArea,
    //     coPayType: product.coPayType,
    //     paymentFrequency: product.paymentFrequency,
    //     discountsApplied: product.discountsApplied,
    //     pharmacyLimit: product.pharmacyLimit,
    //     dental: product.dental,
    //     optical: product.optical,
    //     alternateMedication: product.alternateMedication,
    //     alternateMedicationFilePath: product.alternateMedicationFilePath,
    //     generalBenefits: product.generalBenefits || {},
    //     inPatientBenefits: product.inPatientBenefits || {},
    //     outPatientBenefits: product.outPatientBenefits || {},
    //     maternityBenefits: product.maternityBenefits || {},
    //     additionalBenefits: product.additionalBenefits || {},
    //     totalPremiumWithTaxes: product.totalPremiumWithTaxes || product.premium?.toString() || '0',
    //     totalPremium: product.totalPremium || '0',
    //     basePremium: product.basePremium || '0',
    //     discountAmount: product.discountAmount || '0',
    //     basmah: product.basmah || '0',
    //     tax: product.tax || '0'
    //   },

    //   plan: {
    //     productCode: productCode,
    //     rank: 0,
    //     confidenceScore: 0,
    //     commission: 0,
    //     reason: '',
    //     quoteSource: QuoteSource.Manual
    //   },

    //   logo: this.drawerService.decideLogo(product.product?.insurer),
    //   colorClass: this.drawerService.decideColorClass(product.product?.colorCode),

    //   favorited: false,
    //   hideConfidenceScore: true
    // };

    // this.drawerService.updateProductCards(cards => [...cards, mappedCard]);
    // this.drawerService.markAsChanged();
  }

  onRefresh(): void {
    console.log('Refreshing products...');
    // Implement your refresh logic here
  }

  sendMessage(): void {
    if (!this.chatInputText.trim()) return;

    const currentMessages = this.chatMessages;
    const currentHasStarted = this.hasChatStarted;

    if (!currentHasStarted) {
      this.hasChatStarted = true;
    }

    const userQuery = this.chatInputText.trim();

    // Add user message to the chat
    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user' as const,
      text: userQuery,
      timestamp: new Date()
    };

    this.chatMessages = [...currentMessages, userMessage];

    this.chatInputText = '';
    this.scrollToBottom();

    // Build policies dynamically from stored version quotes
    const policies = this.buildPoliciesFromQuotes();
    const clientProfile = this.drawerService.clientProfileResponse();
    const payload: any = {
      leadId: this.leadData?.leadId,
      leadStatus: '2',
      userQuery,
      policies,
      basicInfo: {
        ...clientProfile?.basic,
        preferredPolicyStartDate: DateTimeConversionHelper.convertToDateTime(
          clientProfile?.basic.preferredPolicyStartDate ?? null
        )
      },

      primaryApplicant: {
        ...clientProfile?.primaryApplicant,
        dob: DateTimeConversionHelper.convertToDateTime(
          clientProfile?.primaryApplicant?.dob ?? null
        )
      },

      dependentApplicants: (clientProfile?.dependentApplicants || []).map(
        (dependent: any) => ({
          ...dependent,
          dob: DateTimeConversionHelper.convertToDateTime(dependent.dob ?? null)
        })
      ),
      emirates: this.drawerService.emirate() || [],
    };

    this.apiService.getAiChatResponse(payload).subscribe({
      next: (res) => this.handleAiApiResponse(res, true),
      error: () => console.error('AI chat failed')
    });
  }

  // NEW CODE
  // sendMessage(): void {
  //   if (!this.chatInputText.trim()) return;

  //   const currentMessages = this.chatMessages;
  //   const currentHasStarted = this.hasChatStarted;

  //   if (!currentHasStarted) {
  //     this.hasChatStarted = true;
  //   }

  //   const userQuery = this.chatInputText.trim();

  //   // Add user message to the chat
  //   const userMessage = {
  //     id: `user_${Date.now()}`,
  //     type: 'user' as const,
  //     text: userQuery,
  //     timestamp: new Date()
  //   };

  //   this.chatMessages = [...currentMessages, userMessage];

  //   this.chatInputText = '';
  //   this.scrollToBottom();

  //   const payload: AiChatRequestUpdated = {
  //     userId: this.leadData?.ownerAgentId,
  //     leadId: this.leadData?.leadId,
  //     query: userQuery,
  //   };

  //   this.apiService.getAiChatResponse(payload).subscribe({
  //     next: (res) => this.handleAiApiResponse(res, true),
  //     error: () => console.error('AI chat failed')
  //   });
  // }

  formatChatDate(timestamp?: Date | null): string {
    return timestamp ? new Date(timestamp).toLocaleString() : '';
  }

  private scrollToBottom(): void {
    if (!this.chatScroll) return;

    requestAnimationFrame(() => {
      const el = this.chatScroll.nativeElement;
      el.scrollTop = el.scrollHeight;
    });
  }

  private handleAiApiResponse(res: any, isUserInteraction = false): void {
    const history = isUserInteraction ? [] : (res?.conversationHistory ?? []);

    // Clear only when loading history (not during user interaction)
    if (!isUserInteraction) {
      this.chatMessages = [];
    }

    // No history - show greeting
    if (!history.length && !isUserInteraction) {
      this.hasChatStarted = false;
      requestAnimationFrame(() => this.scrollToBottom());
      return;
    }

    // Store topPlans
    if (res?.topPlans?.length) {
      this.topThreePlans = res.topPlans;
    }

    // Load history ONLY on first open / reload
    if (history.length) {
      this.hasChatStarted = true;

      const historyMessages = history.map((item: any, index: number) => ({
        id: `history_${index}`,
        type: item.role === 'assistant' ? 'ai' : 'user',
        text: item.content,
        timestamp: new Date(item.timestamp),
        plans: item.modifiedPlans?.length
          ? this.mapPlans(item.modifiedPlans)
          : undefined
      }));

      this.chatMessages = historyMessages;
    }

    // Append latest AI response ONLY for user interaction
    if (isUserInteraction && res?.assistantResponse) {
      const aiMessage = {
        id: `ai_${Date.now()}`,
        type: 'ai' as const,
        text: res.assistantResponse,
        timestamp: new Date(),
        plans: res.topPlans?.length
          ? this.mapPlans(res.topPlans)
          : undefined
      };

      // Get current messages again (includes the user message we just added)
      const updatedMessages = this.chatMessages;
      this.chatMessages = [...updatedMessages, aiMessage];
    }

    requestAnimationFrame(() => this.scrollToBottom());
  }

  //  CHAT CODE
  // private handleAiApiResponse(res: AiChatResponse, isUserInteraction = false): void {
  //   const history = isUserInteraction ? [] : (res.chatHistory ?? []);

  //   // Clear only when loading history (not during user interaction)
  //   if (!isUserInteraction) {
  //     this.chatMessages = [];
  //   }

  //   // No history - show greeting
  //   if (!history.length && !isUserInteraction) {
  //     this.hasChatStarted = false;
  //     requestAnimationFrame(() => this.scrollToBottom());
  //     return;
  //   }


  //   // Load history ONLY on first open / reload
  //   if (history.length) {
  //     const historyMessages = this.mapAiHistoryToMessages(history);
  //     this.hasChatStarted = historyMessages.length > 0;

  //     this.chatMessages = historyMessages;
  //   }

  //   // Append latest AI response ONLY for user interaction
  //   if (isUserInteraction) {
  //     const assistantText = this.extractAssistantMessage(res);
  //     if (assistantText) {
  //       const aiMessage = {
  //         id: `ai_${Date.now()}`,
  //         type: 'ai' as const,
  //         text: assistantText,
  //       };

  //       // Get current messages again (includes the user message we just added)
  //       const updatedMessages = this.chatMessages;
  //       this.chatMessages = [...updatedMessages, aiMessage];
  //     }
  //   }

  //   requestAnimationFrame(() => this.scrollToBottom());
  // }

  private mapAiHistoryToMessages(history: AiChatHistoryItem[]): ChatMessage[] {
    const mappedMessages: ChatMessage[] = [];

    history.forEach((item: AiChatHistoryItem, index: number) => {
      const userText = item.user?.trim();
      const assistantText = item.assistant?.trim();

      if (userText) {
        mappedMessages.push({
          id: `history_user_${index}`,
          type: 'user',
          text: userText,
        });
      }

      if (assistantText) {
        mappedMessages.push({
          id: `history_ai_${index}`,
          type: 'ai',
          text: assistantText,
        });
      }

      if (!userText && !assistantText && item.content?.trim()) {
        mappedMessages.push({
          id: `history_${index}`,
          type: item.role === 'assistant' ? 'ai' : 'user',
          text: item.content.trim(),
        });
      }
    });

    return mappedMessages;
  }

  private extractAssistantMessage(res: AiChatResponse): string | null {
    if (res.message?.trim()) {
      return res.message.trim();
    }

    if (res.assistantResponse?.trim()) {
      return res.assistantResponse.trim();
    }

    return null;
  }

  private resolveMessageTimestamp(timestamp?: string): Date {
    if (!timestamp) {
      return new Date();
    }

    const parsedDate = new Date(timestamp);
    return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
  }

  private mapPlans(plans: any[]): any[] {
    return plans.map(p => ({
      planId: p.productCode,
      productId: p.productId || p.products?.[0]?.id || p.productHealthDetails?.[0]?.productId,
      planName: p.planName,
      insurer: p.insurer,
      networkName: p.tpa_Network,
      premium: p.totalPremiumWithTaxes ?? 0,
      rank: p.rank,
      confidenceScore: p.confidenceScore,
      commission: p.commission,
      reason: p.reason
    }));
  }

  private resolveQuoteSource(value: unknown): QuoteSource {
    if (typeof value === 'number' && Object.values(QuoteSource).includes(value)) {
      return value as QuoteSource;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'ai') return QuoteSource.AI;
      if (normalized === 'manual') return QuoteSource.Manual;
      if (normalized === 'manualandai') return QuoteSource.ManualAndAI;
    }
    return QuoteSource.Manual;
  }

  private buildPoliciesFromQuotes(): any[] {
    const cards = this.productCards();
    if (!cards.length) {
      return [];
    }

    return cards.map((card: any) => ({
      productCode: card.product?.productCode || card.plan?.productCode || '',
      quoteSource: card.plan?.quoteSource ?? 1
    }));
  }

  // OLD CODE: loadAiConversation using AiChatRequest

  private loadAiConversation(): void {
    // Build policies dynamically from stored version quotes
    const policies = this.buildPoliciesFromQuotes();
    const clientProfile = this.drawerService.clientProfileResponse() as any;
    const payload: any = {
      leadId: this.leadData?.leadId,
      leadStatus: '2',
      userQuery: '',
      policies,
      basicInfo: {
        ...clientProfile?.basic,
        preferredPolicyStartDate: DateTimeConversionHelper.convertToDateTime(
          clientProfile?.basic?.preferredPolicyStartDate ?? null
        )
      },

      primaryApplicant: {
        ...clientProfile?.primaryApplicant,
        dob: DateTimeConversionHelper.convertToDateTime(
          clientProfile?.primaryApplicant?.dob ?? null
        )
      },

      dependentApplicants: (clientProfile?.dependentApplicants || []).map(
        (dependent: any) => ({
          ...dependent,
          dob: DateTimeConversionHelper.convertToDateTime(dependent.dob ?? null)
        })
      ),
      emirates: this.drawerService.emirate() || [],
    };

    this.apiService.getAiChatResponse(payload).subscribe({
      next: (res: any) => this.handleAiApiResponse(res),
      error: (err: any) => console.error('Failed to load AI conversation', err)
    });
  }

  // NEW CHAT CODE

  // private loadAiConversation(): void {

  //   const payload: AiChatRequestUpdated = {
  //     userId: this.leadData?.ownerAgentId,
  //     leadId: this.leadData?.leadId,
  //     query: "",
  //   };

  //   this.apiService.getAiChatResponse(payload).subscribe({
  //     next: (res: AiChatResponse) => this.handleAiApiResponse(res),
  //     error: (err: any) => console.error('Failed to load AI conversation', err)
  //   });
  // }

  private mapPrefToLeadSummary(): void {
    if (!this.prefFormData) return;

    const contactInfo = this.prefFormData.contactInfo || {};
    const basic = this.prefFormData.basic;
    const primary = this.prefFormData.primaryApplicant;
    const dependents = this.prefFormData.dependentApplicants || [];
    const preference = this.prefFormData.preference;

    const insuranceFor = basic?.insuranceFor;
    const familyCount = basic?.familyMemberCount || 0;

    let coverMembers = '';

    if (insuranceFor === 'Self') {
      coverMembers = 'Self';
    }
    else if (insuranceFor === 'Family') {
      coverMembers = `${familyCount}`;
    }
    else if (insuranceFor === 'SelfAndFamily') {
      coverMembers = `Self + ${familyCount}`;
    }

    const mappedQuote: QuoteData = {
      id: this.prefFormData.leadId,
      name: `${contactInfo.firstName} ${contactInfo.lastName ?? ''}`,
      refId: this.prefFormData.leadId,
      isPep: !!(this.prefFormData?.isPep ?? preference?.isPep),

      coverMembers: this.safeValue(coverMembers),

      networkAccess: this.safeValue(
        this.formatNetwork(preference?.preferredNetworkAccess)
      ),

      hospitalList: preference?.preferredProviders?.length
        ? preference.preferredProviders
          .map((p: any) => p.providerName)
          .join(', ')
        : 'NIL',

      copay: preference?.optForCoPay ? 'Yes' : 'No',
      prevInsurer: this.safeValue(preference?.previousInsurer),
      previousPremium: this.safeValue(preference?.previousPremium),
      primaryAge: this.formatAgeWithYears(primary?.age),
      primaryGender: this.safeValue(primary?.gender),
      primaryMaritalStatus: this.safeValue(primary?.maritalStatus),
      primaryEmirate: this.safeValue(primary?.emirateOfVisaInsurance),

      dependents: (dependents || []).map((d: any) => ({
        age: this.formatAgeWithYears(d?.age),
        gender: this.safeValue(d?.gender),
        maritalStatus: this.safeValue(d?.maritalStatus),
        emirate: this.safeValue(d?.emirateOfVisaInsurance)
      }))
    };

    this.drawerService.setQuoteData([mappedQuote]);
  }

  private safeValue(value: any): string {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 0
    ) {
      return 'NIL';
    }

    return value.toString();
  }

  private formatNetwork(value: string): string {
    if (!value) return '';

    return value
      .replace(/([A-Z])/g, ' $1')
      .trim();
  }

  private formatAgeWithYears(age: number): string {
    if (!age && age !== 0) return 'NIL';
    return `${age} Yr${age > 1 ? 's' : ''}`;
  }
  
  formatVersionTime(dateTime: string): string {
    if (!dateTime) return '';
    const dt = new Date(dateTime);

    return new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dt);
  }

  formatVersionDate(dateTime: string): string {
    if (!dateTime) return '';

    const isoMatch = dateTime.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}-${month}-${year}`;
    }

    const dashedMatch = dateTime.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dashedMatch) {
      const [, day, month, year] = dashedMatch;
      return `${day}-${month}-${year}`;
    }

    const dottedMatch = dateTime.match(/^(\d{2})\.(\d{2})\.(\d{2,4})/);
    if (dottedMatch) {
      const [, day, month, year] = dottedMatch;
      const fullYear = year.length === 2 ? `20${year}` : year;
      return `${day}-${month}-${fullYear}`;
    }

    return dateTime;
  }

  formatVersionDateTime(dateTime: string): string {
    if (!dateTime) return '';
    return `${this.formatVersionTime(dateTime)}, ${this.formatVersionDate(dateTime)}`;
  }

  getVersionHistory(): void {
    if (this.drawerService.hasUnsavedChanges()) {
      return;
    }

    if (this.drawerService.versionHistory()?.length > 0) {
      // Keep UI selection in sync with draft/default version
      this.setDefaultVersionSelection();


      // On mount: only fetch quotes if there's a draft version
      const draftVersion = this.drawerService.versionHistory().find((v: any) => v.isDraft === true);
      if (draftVersion) {
        // For draft, call without version parameter
        this.fetchQuotesForVersion(draftVersion.version);
      }
      else if (QUOTE_FLOW() === QuoteFlowEnum.Requote || QUOTE_FLOW() === QuoteFlowEnum.Existing) {
        const currentVersion = this.drawerService.currentVersion();
        this.fetchQuotesForVersion(currentVersion?.version || '');
      }
      else if (this.isVersionHistoryMode || this.drawerService.isVersionHistoryOpen()) {
        const currentVersion = this.drawerService.currentVersion();
        if (currentVersion?.version) {
          this.fetchQuotesForVersion(currentVersion.version);
        }
      }
      else {
        // No draft version - clear product cards
        this.drawerService.setProductCards([]);
      }
    }
  }
getQuotesInfoOnlyByVersion({ leadInfoId, quoteversion }: { leadInfoId: string; quoteversion: string }): void {
    const payload = { leadInfoId, quoteversion };
    this.drawerService.subscribeWithCleanup(
      this.drawerService.getQuotesOnlyByVersion(payload),
      (response: any) => {
        this.drawerService.setQuotesStoredForLead(response.body || []);
        if (this.isComparison) {
          return
        }
      },
      this.destroyRef
    );
  }

}


